import { MessagesSearchResponse } from '@lio/api-types';
import { Box } from '@mui/system';
import { EventDivider } from '@pages/PolicyDetails/dialogs/MessagesDialog/components/EventDivider';
import { MessageBubble } from '@pages/PolicyDetails/dialogs/MessagesDialog/components/MessageBubble';
import { useState, useMemo, useCallback, useEffect, useRef, ReactElement } from 'react';
import { InView } from 'react-intersection-observer';

type BaseMessage = MessagesSearchResponse['items'][number];
export type TMessageExtended = Omit<BaseMessage, 'createdByUser'> & {
  type?: 'SYSTEM';
  createdByUser?: BaseMessage['createdByUser'];
};

export type VirtualizedMessagesProps = {
  messages: TMessageExtended[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  loadOlder?: () => void;
  loadNewer: () => void;
  hasNewer: boolean;
  hasOlder?: boolean;
  isLoadingNewer: boolean;
  isLoadingOlder: boolean;
  defaultItemHeight?: number;
  overscanPx?: number;
  jumpToMessageId?: string;
  renderMessage?: (message: MessagesSearchResponse['items'][number]) => ReactElement;
  fullWidthMode?: boolean;
};

export const VirtualizedMessages = ({
  messages,
  scrollRef,
  loadOlder,
  hasNewer,
  hasOlder,
  isLoadingNewer,
  loadNewer,
  isLoadingOlder,
  defaultItemHeight = 60,
  overscanPx = 100,
  jumpToMessageId,
  renderMessage,
  fullWidthMode,
}: VirtualizedMessagesProps) => {
  const [range, setRange] = useState({ start: 0, end: 0 });
  const [initialViewLoaded, setInitialViewLoaded] = useState(false);

  const [heights, setHeights] = useState<Record<string, number>>({});

  const [lastJumpedToId, setLastJumpedToId] = useState<string | undefined>(undefined);

  // if jumpToMessageId is given ensure that it is the center of the shown items
  // handles jump to id when search result is clicked on - first needs to ensure item is in the rendered view
  useEffect(() => {
    if (!jumpToMessageId) return;
    if (lastJumpedToId === jumpToMessageId) return;
    const index = messages.findIndex((m) => m.id === jumpToMessageId);
    if (index === -1) {
    }
    const middle = Math.floor((range.start + range.end) / 2);
    if (index < middle) {
      setRange({ start: index, end: middle });
    } else {
      setRange({ start: middle, end: index + 1 });
    }

    setTimeout(() => {
      const el = document.getElementById(`message-${jumpToMessageId}`);

      if (el && scrollRef.current) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      setLastJumpedToId(jumpToMessageId);
    }, 300);
  }, [jumpToMessageId, lastJumpedToId, messages, range.end, range.start, scrollRef]);

  const cumulativeHeights = useMemo(() => {
    const arr: number[] = new Array(messages.length + 1).fill(0);
    for (let i = 0; i < messages.length; i++) {
      const id = messages[i].id;
      const h = heights[id] || defaultItemHeight;
      arr[i + 1] = arr[i] + h;
    }
    return arr;
  }, [messages, heights, defaultItemHeight]);
  const totalHeight = cumulativeHeights[cumulativeHeights.length - 1];

  const findIndex = useCallback(
    (offset: number) => {
      if (offset <= 0) return 0;
      let lo = 0;
      let hi = cumulativeHeights.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (cumulativeHeights[mid] <= offset) lo = mid + 1;
        else hi = mid;
      }
      return Math.max(0, lo - 1);
    },
    [cumulativeHeights],
  );

  const recalcRange = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, clientHeight } = el;
    const start = findIndex(scrollTop - overscanPx);
    const end = findIndex(scrollTop + clientHeight + overscanPx) + 1;
    setRange({ start, end: Math.min(end, messages.length) });
  }, [findIndex, messages.length, overscanPx, scrollRef]);

  // initial & dependency-based recalculation
  useEffect(() => {
    recalcRange();
  }, [recalcRange, messages.length, heights]);

  // scroll listener
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => recalcRange();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [recalcRange, scrollRef]);

  const { start, end } = range;
  const slice = messages.slice(start, end);
  const topSpacer = cumulativeHeights[start];
  const bottomSpacer = totalHeight - cumulativeHeights[end];

  useEffect(() => {
    if (range.start > 0 && !initialViewLoaded) {
      // this is to prevent the InView (load newer + older ) from auto firing as component loads the first view
      setInitialViewLoaded(true);
    }
  }, [range, initialViewLoaded]);

  return (
    <>
      {messages.length > 0 && (
        <InView
          as='div'
          onChange={(inView) => {
            if (loadOlder && hasOlder && initialViewLoaded && inView && !isLoadingOlder) {
              loadOlder();
            }
          }}
          style={{ height: 1 }}
        />
      )}
      <Box style={{ height: topSpacer }} />
      {slice.map((m: any) => (
        <MeasuredMessage
          fullWidthMode={fullWidthMode}
          key={m.id}
          message={m}
          renderMessage={renderMessage}
          reportHeight={(h) => {
            if (h <= 0) return;
            setHeights((prev) => (prev[m.id] === h ? prev : { ...prev, [m.id]: h }));
          }}
        />
      ))}
      <Box style={{ height: bottomSpacer }} />
      {messages.length > 0 && (
        <InView
          as='div'
          onChange={(inView) => {
            if (initialViewLoaded && inView && hasNewer && !isLoadingNewer) {
              loadNewer();
            }
          }}
          style={{ height: 1 }}
        />
      )}
    </>
  );
};

const MeasuredMessage = ({
  message,
  reportHeight,
  renderMessage,
  fullWidthMode,
}: {
  message: any;
  reportHeight: (h: number) => void;
  renderMessage?: (message: MessagesSearchResponse['items'][number]) => ReactElement;
  fullWidthMode?: boolean;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (ref.current) {
      const h = ref.current.getBoundingClientRect().height;
      if (h > 0) reportHeight(h);
    }
  }, [reportHeight]);
  return (
    <Box ref={ref} id={`message-${message.id}`}>
      {renderMessage ? (
        renderMessage(message)
      ) : message?.type === 'SYSTEM' ? (
        <EventDivider key={message.id} date={message.createdAt} text={message.body} />
      ) : (
        <MessageBubble
          fullWidthMode={fullWidthMode}
          message={message}
          align={message.createdByUser?.lioUsers?.length ? 'left' : 'right'}
        />
      )}
    </Box>
  );
};
